<template>
  <div class="ms-container full-width experience">
    <div class="experience-table-controls">
      <div class="experience-table-controls-left">
        <experience-filter />
      </div>
      <div class="experience-table-controls-right">
        <i-button
          class="export-documents"
          @click="downloadModal = true"
          type="primary"
          size="default"
        >
          Скачать документы
        </i-button>
        <i-button
          class="export-table-button"
          @click="exportTable"
          type="success"
          size="default"
        >
          Экспорт таблицы
        </i-button>
      </div>
    </div>
    <div class="experience-active-filters">
      <div class="ms-help">
        {{
          activeFilters.length
            ? "Активные фильтры:"
            : "Активные фильтры отсутствуют"
        }}
      </div>
      <div class="experience-active-filters-list">
        <tag
          v-for="filter in activeFilters"
          :key="filter.name"
          closable
          @on-close="closeFilterHandler(filter.name)"
        >
          {{ filter.label }}
        </tag>
      </div>
      <i-button
        @click="clearFilters"
        size="small"
        class="clear-button"
        type="default"
        v-if="isFiltersActive"
      >
        Сбросить фильтры
      </i-button>
    </div>
    <div class="experience-table">
      <inline-loader :loading="loading" v-if="loading && !nodata" />
      <div class="ms-nodata ms-text" v-else-if="!loading && nodata">
        Не удалось загрузить таблицу опыта работ
      </div>
      <div class="experience-table-container" v-else>
        <i-table
          ref="experienceTable"
          no-data-text="Проекты отсутствуют"
          border
          :columns="columns"
          :data="filteredData"
          :disabled-hover="true"
          size="small"
          :height="maxTableHeight"
          :loading="tableLoading"
        >
          <template slot-scope="{ row }" slot="action">
            <i-button
              type="primary"
              shape="circle"
              icon="md-create"
              @click="editContract(row)"
            />
          </template>
        </i-table>

        <div class="experience-table-pagination">
          <page
            :total="total"
            :current="current"
            :page-size="count"
            @on-change="changePage"
            @on-page-size-change="changePageSize"
            show-sizer
            :page-size-opts="pageSizes"
          />
        </div>
      </div>
    </div>

    <modal v-model="downloadModal" width="360">
      <p slot="header" class="ms-text" style="text-align: center">
        <icon type="md-cloud-download" />
        <span style="margin-left: 4px">Скачивание материалов</span>
      </p>
      <div style="text-align: center">
        <p class="ms-text">Выберите типы материалов для скачивания</p>
        <div class="download-modal-content">
          <checkbox-group v-model="downloadTypes">
            <checkbox
              v-for="type in objectTypes"
              :key="type.type"
              :label="type.type"
              border
            >
              {{ type.name }}
            </checkbox>
          </checkbox-group>
        </div>
      </div>
      <div slot="footer">
        <i-button
          :loading="downloading"
          type="primary"
          size="default"
          long
          @click="exportDocuments"
        >
          {{ downloading ? "Подготовка к скачиванию..." : "Скачать документы" }}
        </i-button>
      </div>
    </modal>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep'
import debounce from 'lodash.debounce'
import InlineLoader from '@/components/loaders/inline-loader'
import ExperienceFilter from '@/components/filters/experience'
import { downloadWithWidget, downloadFile, getActualHeight } from '@/utils'
import { mainUrl } from '@/config/urls'
import { columns } from '@/config/experience'
import {
  experienceFilters,
  getExperienceUrl,
  experienceConfig
} from '@/config/filters'
import { objectTypes } from '@/config/resources'

const activeFiltersFn = (self) => ({ name }) => {
  const value = self.$store.state.filters.experience[name]
  const statuses = self.$store.state.contractStatuses

  if (name === 'status') {
    if (value.length !== 2) return true

    return value.every((id) => statuses.includes(id))
  } else {
    if (typeof value === 'number' && value > 0) return true
    if (typeof value === 'string' && value !== 'all') return true
    if (typeof value === 'object' && value.length) return true
  }

  return false
}

export default {
  components: {
    InlineLoader,
    ExperienceFilter
  },
  data () {
    return {
      loading: true,
      nodata: false,
      projects: [],
      token: this.$store.state.auth.token,
      prevFilters: null,
      maxTableHeight: 640,
      current: 1,
      count: 15,
      total: 20,
      pageSizes: [15, 25, 40],
      downloadModal: false,
      downloading: false,
      downloadTypes: objectTypes.map((t) => t.type),
      objectTypes,
      columns
    }
  },
  computed: {
    activeFilters () {
      return experienceFilters.slice(1).filter(activeFiltersFn(this))
    },
    isFiltersActive () {
      return experienceFilters.slice(1).some(activeFiltersFn(this))
    },
    filteredData () {
      const currentIndex = (this.current - 1) * this.count

      return this.projects.slice(currentIndex, currentIndex + this.count)
    },
    tableLoading () {
      return this.$store.state.experience.tableLoading
    }
  },
  watch: {
    '$store.state.filters.experience': {
      handler: debounce(async function (val) {
        if (JSON.stringify(val) !== JSON.stringify(this.prevFilters)) {
          await this.getUniqueValues()
          await this.getData(true)
          this.current = 1
          this.prevFilters = cloneDeep(val)
        }
      }, 1000),
      deep: true,
      immediate: false
    },
    '$store.state.filters.company': async function () {
      await this.getUniqueValues()
      this.getData(true)
    }
  },
  methods: {
    changePage (page) {
      this.current = page
    },
    changePageSize (size) {
      this.count = size
    },
    closeFilterHandler (field) {
      if (this.tableLoading) return

      const value = experienceConfig[field]

      this.$store.commit('CHANGE_FILTER', {
        type: 'experience',
        field,
        value
      })
    },
    async getUniqueValues () {
      try {
        const url = getExperienceUrl(
          `page/experience?all_unique_values=true&`,
          this.$store.state.filters
        )
        const values = await this.$store.dispatch('GET_REQUEST', { url })
        this.$store.commit('SET_UNIQUE_VALUES', values)

        const value = !this.$store.state.experience.uniqueValuesUpdated
        this.$store.commit('SET_UNIQUE_VALUES_UPDATED', value)
      } catch (error) {
        console.log(error)
      }
    },
    async getData (noloading) {
      if (this.tableLoading) return

      try {
        if (!noloading) this.loading = true
        else this.$store.commit('SET_EXP_TABLE_LOADING', true)

        const url = getExperienceUrl(
          `page/experience?`,
          this.$store.state.filters
        )
        const projects = await this.$store.dispatch('GET_REQUEST', { url })
        this.projects = projects.map((p, i) => ({ ...p, index: i + 1 }))
        this.total = this.projects.length
        this.loading = false
        this.$store.commit('SET_EXP_TABLE_LOADING', false)
      } catch (error) {
        console.log(error)
        this.loading = false
        this.$store.commit('SET_EXP_TABLE_LOADING', false)
        this.nodata = true
      }
    },
    editContract ({ id }) {
      this.$store.commit('SET_COME_FROM', null)

      const route = this.$router.resolve({
        path: `/app/contracts/${id}/overview`
      })

      window.open(route.href, '_blank')
    },
    exportTable () {
      const baseUrl = getExperienceUrl(
        `page/experience?`,
        this.$store.state.filters
      )
      const url = `${mainUrl}/api/${baseUrl}&format=excel&access_token=${this.token}`

      downloadFile(url)
    },
    exportDocuments () {
      const baseUrl = getExperienceUrl(
        `page/all_document?`,
        this.$store.state.filters
      )
      const params = `with_filters=true&as_file=true&object_type=${this.downloadTypes.join(
        ','
      )}`
      const url = `${mainUrl}/api/${baseUrl}&${params}&access_token=${this.token}`

      downloadWithWidget(this, url)

      this.downloading = true
    },
    clearFilters () {
      this.$store.commit('CLEAR_FILTERS', 'experience')
    },
    changeTableHeight () {
      const height = getActualHeight()

      this.maxTableHeight = height - 192
    }
  },
  async created () {
    this.loading = true
    await this.getUniqueValues()
    this.getData()

    this.changeTableHeight()
    window.addEventListener('resize', this.changeTableHeight)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.changeTableHeight)
  }
}
</script>

<style lang="less">
.experience {
  .ivu-table-cell {
    word-break: break-word;
    white-space: pre-line;
  }

  &-table-controls {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    &-left,
    &-right {
      display: flex;
      justify-content: space-between;
    }

    .export-table-button {
      margin-left: 16px;
    }
  }

  &-active-filters {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    .ms-help {
      line-height: 24px;
      margin-right: 16px;
    }

    .ivu-tag {
      margin: 0;

      &:not(:first-child) {
        margin-left: 4px;
      }
    }

    .clear-button {
      margin-left: 16px;
    }
  }

  &-table-pagination {
    margin-top: 16px;
    display: flex;
    justify-content: center;
  }

  th {
    .ivu-table-cell {
      padding: 0 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &.custom-filter .ivu-table-cell {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
